// ─── React Core ──────────────────────────────────────────────
import React, { forwardRef, useRef, useState } from "react";

// ─── Utils ───────────────────────────────────────────────────
import classNames from "classnames";

// ─── Styles ──────────────────────────────────────────────────
import styles from "./styles.module.scss";

// ─── Enum for Input Type ─────────────────────────────────────
export enum InputType {
  DEFAULT = "DEFAULT",
  NUMBER = "NUMBER",
}

// ─── Props Interface ────────────────────────────────────────
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  defaultValue?: string | number;
  maxCharacters?: number;
  maxValue?: number;
  type?: InputType;
  required?: boolean;
  description?: string;
  errorMessage?: string;
}

// ─── TextInput Component ─────────────────────────────────────
// Designed for React Hook Form only: register, ref, and defaultValue
const TextInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      label = "",
      defaultValue = "",
      maxCharacters = 100,
      maxValue,
      required,
      type = InputType.DEFAULT,
      description = "",
      errorMessage,
      className,
      onKeyPress,
      ...props
    },
    ref
  ) => {
    const internalRef = useRef<HTMLInputElement | null>(null);
    const [focused, setFocused] = useState(false);

    // Only allow number input if type is NUMBER
    const validateKey = (key: string) => {
      return /[0-9.]/.test(key);
    };

    // Combine input-specific classes
    const inputClass = classNames(styles.input, {
      [styles.number]: type === InputType.NUMBER,
    });

    const containerClass = classNames(styles.container, className, {
      [styles.error]: errorMessage,
    });

    return (
      <div className={styles.container_outer}>
        {/* Optional Label */}
        {label && (
          <div className={styles.header}>
            <p className={classNames(styles.p1, styles.g400)}>{label}</p>
          </div>
        )}

        <div className={containerClass}>
          <input
            ref={(el) => {
              internalRef.current = el;
              if (typeof ref === "function") ref(el);
              else if (ref) ref.current = el;
            }}
            className={inputClass}
            defaultValue={defaultValue}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyPress={(event) => {
              const currentValue = internalRef.current?.value || "";

              // Prevent overflow of max characters
              if (
                maxCharacters &&
                String(currentValue).length >= maxCharacters
              ) {
                event.preventDefault();
              }

              // Prevent invalid number entry
              if (type === InputType.NUMBER && !validateKey(event.key)) {
                event.preventDefault();
              }

              onKeyPress?.(event);
            }}
            style={{ width: "100%", minHeight: "16px" }}
            {...props}
          />

          {/* Description below input */}
          {description && (
            <p className={classNames(styles.p4, styles.g400)}>{description}</p>
          )}
        </div>

        {/* Optional error message */}
        {errorMessage && (
          <div className={styles.error_container}>
            <p dir="rtl" className={styles.error_text}>
              {errorMessage}
            </p>
          </div>
        )}
      </div>
    );
  }
);

TextInput.displayName = "TextInput";

export default React.memo(TextInput);
